/**
 * Vehicle-related labels (fuel type, transmission, condition)
 * Dynamic mapping from JSON based on current language
 * Uses getLanguage() to determine which locale to use
 */

import {
  VehicleFuelType,
  TransmissionType,
  ConditionGrade,
  VehicleStatus,
} from '@/types/enums';
import svVehicleCard from '../locales/sv/vehicleCard.json';
import enVehicleCard from '../locales/en/vehicleCard.json';
import { getLanguage, type Language } from '@/utils/languageUtils';

/**
 * Helper function to get the correct locale based on language
 */
const getVehicleCardLocale = (lang?: Language) => {
  const currentLang = lang || getLanguage();
  return currentLang === 'en' ? enVehicleCard : svVehicleCard;
};

/**
 * Fuel Type labels - dynamically uses current language
 */
export const fuelTypeLabels = (lang?: Language): Record<VehicleFuelType, string> => {
  const locale = getVehicleCardLocale(lang);
  return {
    [VehicleFuelType.GASOLINE]: locale.fuelType.gasoline,
    [VehicleFuelType.DIESEL]: locale.fuelType.diesel,
    [VehicleFuelType.ELECTRIC]: locale.fuelType.electric,
    [VehicleFuelType.ETHANOL]: locale.fuelType.ethanol,
    [VehicleFuelType.HYBRID]: locale.fuelType.hybrid,
    [VehicleFuelType.PLUG_IN_HYBRID]: locale.fuelType.plug_in_hybrid,
    [VehicleFuelType.GAS]: locale.fuelType.gas,
    [VehicleFuelType.OTHER]: locale.fuelType.other,
  };
};

/**
 * Transmission Type labels - dynamically uses current language
 */
export const transmissionTypeLabels = (lang?: Language): Record<TransmissionType, string> => {
  const locale = getVehicleCardLocale(lang);
  return {
    [TransmissionType.AUTOMATIC]: locale.transmission.automatic,
    [TransmissionType.MANUAL]: locale.transmission.manual,
    [TransmissionType.SEMI_AUTOMATIC]: locale.transmission.semi_automatic,
    [TransmissionType.CVT]: locale.transmission.cvt,
    [TransmissionType.OTHER]: locale.transmission.other,
  };
};

/**
 * Condition Grade labels - dynamically uses current language
 */
export const conditionGradeLabels = (lang?: Language): Record<ConditionGrade, string> => {
  const locale = getVehicleCardLocale(lang);
  return {
    [ConditionGrade.NEW]: locale.condition.new,
    [ConditionGrade.LIKE_NEW]: locale.condition.like_new,
    [ConditionGrade.EXCELLENT]: locale.condition.excellent,
    [ConditionGrade.GOOD]: locale.condition.good,
    [ConditionGrade.NORMAL]: locale.condition.normal,
    [ConditionGrade.FAIR]: locale.condition.fair,
    [ConditionGrade.POOR]: locale.condition.poor,
    [ConditionGrade.RENOVATION_OBJECT]: locale.condition.renovation_object,
    [ConditionGrade.OTHER]: locale.condition.other,
  };
};

/**
 * Vehicle Status labels - dynamically uses current language
 */
export const vehicleStatusLabels = (lang?: Language): Record<VehicleStatus, string> => {
  const locale = getVehicleCardLocale(lang);
  return {
    [VehicleStatus.AVAILABLE]: locale.vehicleStatus.available,
    [VehicleStatus.RESERVED]: locale.vehicleStatus.reserved,
    [VehicleStatus.SOLD]: locale.vehicleStatus.sold,
    [VehicleStatus.AUCTION]: locale.vehicleStatus.auction,
    [VehicleStatus.HIDDEN]: locale.vehicleStatus.hidden,
  };
};

/**
 * Body Type labels - dynamically uses current language
 */
export const bodyTypeLabels = (lang?: Language): Record<string, string> => {
  const locale = getVehicleCardLocale(lang);
  return locale.bodyType;
};

/**
 * Color labels - dynamically uses current language
 */
export const colorLabels = (lang?: Language): Record<string, string> => {
  const locale = getVehicleCardLocale(lang);
  return locale.color;
};

/**
 * Drive Type labels - for create vehicle card manually
 */
export const driveTypeLabels = (lang?: Language): Record<string, string> => {
  const locale = getVehicleCardLocale(lang);
  return (locale as any).driveType || {};
};

/**
 * Vehicle Type labels (car, mc)
 */
export const vehicleTypeLabels = (lang?: Language): Record<string, string> => {
  const locale = getVehicleCardLocale(lang);
  return (locale as any).vehicleType || {};
};

/** Create manual fuel type keys (includes ethanol, gas) */
export const CREATE_MANUAL_FUEL_KEYS = ['gasoline', 'diesel', 'electric', 'hybrid', 'plug_in_hybrid', 'ethanol', 'gas'] as const;

/** Create manual body type keys (for cars) */
export const CREATE_MANUAL_BODY_KEYS = ['sedan', 'kombi', 'suv', 'halvkombi', 'coupe', 'cabriolet', 'minibuss', 'pickup'] as const;

/** Create manual drive type keys */
export const CREATE_MANUAL_DRIVE_KEYS = ['fwd', 'rwd', '4wd', 'awd'] as const;

/** Create manual color keys (subset) */
export const CREATE_MANUAL_COLOR_KEYS = ['white', 'black', 'gray', 'silver', 'blue', 'red', 'green', 'yellow', 'orange', 'brown', 'beige'] as const;

/** Map backend color values (Swedish/English) to vehicleCard color keys */
const COLOR_KEY_MAP: Record<string, string> = {
  vit: 'white', svart: 'black', blå: 'blue', blue: 'blue', silver: 'silver',
  röd: 'red', red: 'red', grå: 'gray', gray: 'gray', grön: 'green', green: 'green',
  gul: 'yellow', yellow: 'yellow', orange: 'orange', brun: 'brown', brown: 'brown',
  beige: 'beige', lila: 'purple', rosa: 'pink', white: 'white', black: 'black',
  guld: 'gold', gold: 'gold', turkos: 'turquoise', burgundy: 'burgundy',
  koppar: 'copper', copper: 'copper',
  'grön metallisk': 'green_metallic', 'blå metallisk': 'blue_metallic',
  'röd metallisk': 'red_metallic', 'svart metallisk': 'black_metallic',
  'vit metallisk': 'white_metallic', 'silver metallisk': 'silver_metallic',
};

/**
 * Get localized color label from backend value (handles Swedish/English)
 */
export const getColorLabel = (backendValue: string | undefined | null, lang?: Language): string => {
  if (!backendValue || typeof backendValue !== 'string') return '';
  const key = COLOR_KEY_MAP[backendValue.toLowerCase().trim()] || backendValue.toLowerCase();
  const labels = colorLabels(lang);
  return labels[key] || backendValue;
};

/**
 * Get localized body type label from backend value
 */
export const getBodyTypeLabel = (backendValue: string | undefined | null, lang?: Language): string => {
  if (!backendValue || typeof backendValue !== 'string') return '';
  const key = backendValue.toLowerCase().trim();
  const labels = bodyTypeLabels(lang);
  return labels[key] || backendValue;
};

/**
 * Reverse lookup: Label -> VehicleFuelType (auto-generated from same source)
 * Uses current language to determine which labels to use
 */
export const labelToFuelType = (lang?: Language): Record<string, VehicleFuelType> => {
  const labels = fuelTypeLabels(lang);
  return Object.fromEntries(
    Object.entries(labels).map(([enumKey, label]) => [label, enumKey as VehicleFuelType])
  ) as Record<string, VehicleFuelType>;
};

/**
 * Reverse lookup: Label -> TransmissionType (auto-generated from same source)
 * Uses current language to determine which labels to use
 */
export const labelToTransmission = (lang?: Language): Record<string, TransmissionType> => {
  const labels = transmissionTypeLabels(lang);
  return Object.fromEntries(
    Object.entries(labels).map(([enumKey, label]) => [label, enumKey as TransmissionType])
  ) as Record<string, TransmissionType>;
};

/**
 * Reverse lookup: Label -> ConditionGrade (auto-generated from same source)
 * Uses current language to determine which labels to use
 */
export const labelToCondition = (lang?: Language): Record<string, ConditionGrade> => {
  const labels = conditionGradeLabels(lang);
  return Object.fromEntries(
    Object.entries(labels).map(([enumKey, label]) => [label, enumKey as ConditionGrade])
  ) as Record<string, ConditionGrade>;
};

/**
 * Locale-agnostic reverse lookups for the three ENUM selects.
 *
 * The card/test editors seed their select state with labels in the language that was
 * active when the dialog opened, but the seeding effect does not depend on `language` -
 * so switching language with the dialog open leaves e.g. "Bensin" in state while save
 * reverses against the English map. The label then missed, the raw Swedish string was
 * sent, and the backend's enum column rejected it with a 500.
 *
 * Merging both locales' reverse maps removes the race without re-seeding (which would
 * discard whatever the admin had already typed). The label sets do not collide: where sv
 * and en share a spelling ("Diesel", "Hybrid", "Plug-in hybrid", "CVT") they also share
 * the enum value.
 */
const mergeLocaleMaps = <T extends string>(
  build: (lang: Language) => Record<string, T>,
): Record<string, T> => ({ ...build('en'), ...build('sv') });

/** Label -> VehicleFuelType, accepting either locale's spelling. */
export const labelToFuelTypeAnyLanguage = (): Record<string, VehicleFuelType> =>
  mergeLocaleMaps(labelToFuelType);

/** Label -> TransmissionType, accepting either locale's spelling. */
export const labelToTransmissionAnyLanguage = (): Record<string, TransmissionType> =>
  mergeLocaleMaps(labelToTransmission);

/** Label -> ConditionGrade, accepting either locale's spelling. */
export const labelToConditionAnyLanguage = (): Record<string, ConditionGrade> =>
  mergeLocaleMaps(labelToCondition);

/**
 * Reverse lookup: Label -> technical key for body type (auto-generated from same source)
 * Uses current language to determine which labels to use
 */
export const labelToBodyType = (lang?: Language): Record<string, string> => {
  const labels = bodyTypeLabels(lang);
  return Object.fromEntries(
    Object.entries(labels).map(([techKey, label]) => [label, techKey])
  ) as Record<string, string>;
};

/**
 * Reverse lookup: Label -> technical key for color (auto-generated from same source)
 * Uses current language to determine which labels to use
 */
export const labelToColor = (lang?: Language): Record<string, string> => {
  const labels = colorLabels(lang);
  return Object.fromEntries(
    Object.entries(labels).map(([techKey, label]) => [label, techKey])
  ) as Record<string, string>;
};
